import { computed, inject } from '@angular/core';
import { MoverRole } from '@app/move/enums/mover-role.enum';
import { DateUtils, DbUtils } from '@core/utils';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Transaction } from '@smooved-lib/transaction/classes/transaction';
import { TransactionService } from '@smooved-lib/transaction/services/transaction.service';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { getMovingDateState } from '@app/move/state/move.reducer';

type TransactionState = {
    isLoading: boolean;
    transaction?: Transaction;
};

const defaultState: TransactionState = {
    isLoading: false,
    transaction: null,
};

export const TransactionStore = signalStore(
    { providedIn: 'root' },
    withState(defaultState),
    withComputed((state) => {
        // old store to get transaction info for the REA
        const store = inject(Store);

        const getMovingDateFromMove = toSignal(store.select(getMovingDateState));

        const getActiveRole = () => (!!state.transaction()?.transferee ? MoverRole.Transferee : MoverRole.Leaver);
        const getLeaverId = () => DbUtils.getStringId(state.transaction()?.leaver);

        return {
            transactionId: computed(() => DbUtils.getStringId(state.transaction())),
            leaverId: computed(() => getLeaverId()),
            activeRole: computed(() => getActiveRole()),
            isLeaver: computed(() => getActiveRole() === MoverRole.Leaver),
            movingDateTodayOrInThePast: computed(() => {
                let movingDate = state.transaction()?.movingDate || getMovingDateFromMove();
                if (!movingDate) return false;
                return DateUtils.isTodaySameOrAfter(movingDate);
            }),
        };
    }),
    withMethods((store, transactionService = inject(TransactionService)) => {
        const setTransaction = (transaction: Transaction) => {
            patchState(store, { transaction: new Transaction(transaction) });
        };
        const load = async (transactionId: string) => {
            patchState(store, { isLoading: true });
            try {
                const transaction = await firstValueFrom(transactionService.getTransaction(transactionId));
                patchState(store, {
                    isLoading: false,
                    transaction: new Transaction(transaction),
                });
            } catch (_) {
                patchState(store, { isLoading: false });
            }
        };
        const patch = async (transactionId: string, payload: any) => {
            patchState(store, { isLoading: true });
            try {
                await firstValueFrom(transactionService.updateTransaction(transactionId, payload));
                await load(transactionId);
            } catch (_) {
                patchState(store, { isLoading: false });
            }
        };

        return {
            reset() {
                patchState(store, defaultState);
            },
            setTransaction,
            load,
            patch,
        };
    }),
);
